import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useProfile";
import { teamApi, type SetSalaryInput, type UpdateMemberInput } from "@/lib/team/api";
import type { RoleValue } from "@/lib/team/types";

function useClinicKey() {
  const { data: profile } = useProfile();
  return profile?.current_clinic_id ?? null;
}

export function useTeamMembers(enabled = true) {
  const clinicId = useClinicKey();
  return useQuery({ queryKey: ["team", clinicId, "members"], queryFn: teamApi.listMembers, enabled: !!clinicId && enabled });
}

export function useTeamMember(userId: string | undefined) {
  const clinicId = useClinicKey();
  return useQuery({
    queryKey: ["team", clinicId, "member", userId],
    queryFn: () => teamApi.getMember(userId!),
    enabled: !!clinicId && !!userId,
  });
}

export function useMemberPermissions(userId: string | undefined) {
  const clinicId = useClinicKey();
  return useQuery({
    queryKey: ["team", clinicId, "permissions", userId],
    queryFn: () => teamApi.getPermissions(userId!),
    enabled: !!clinicId && !!userId,
  });
}

export function useMemberCompensation(userId: string | undefined, enabled = true) {
  const clinicId = useClinicKey();
  return useQuery({
    queryKey: ["team", clinicId, "compensation", userId],
    queryFn: () => teamApi.getCompensation(userId!),
    enabled: enabled && !!clinicId && !!userId,
  });
}

export function useTeamFeatures() {
  return useQuery({ queryKey: ["team", "features"], queryFn: teamApi.listFeatures, staleTime: 1000 * 60 * 30 });
}

function useTeamMutation<TVars>(fn: (vars: TVars) => Promise<void>) {
  const queryClient = useQueryClient();
  const clinicId = useClinicKey();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["team", clinicId] });
      await queryClient.invalidateQueries({ queryKey: ["user-permissions-map"] });
    },
  });
}

export const useUpdateMember = () => useTeamMutation((input: UpdateMemberInput) => teamApi.updateMember(input));
export const useSetMemberRole = () =>
  useTeamMutation(({ userId, role }: { userId: string; role: Exclude<RoleValue, "owner"> }) => teamApi.setRole(userId, role));
export const useSetMemberPermission = () =>
  useTeamMutation(({ userId, feature, action, allowed }: { userId: string; feature: string; action: string; allowed: boolean }) =>
    teamApi.setPermission(userId, feature, action, allowed));
export const useResetMemberPermissions = () => useTeamMutation((userId: string) => teamApi.resetPermissions(userId));
export const useSetMemberSalary = () => useTeamMutation((input: SetSalaryInput) => teamApi.setSalary(input));
